"use client";

import { useEffect, useState } from "react";
import { Star } from "lucide-react";

interface Review {
  author: string;
  rating: number;
  text: string;
  relativeTime: string;
  profilePhoto: string | null;
}
interface ReviewsData {
  configured: boolean;
  rating?: number | null;
  total?: number | null;
  googleUrl?: string | null;
  reviews?: Review[];
}

// Reliable link to the store's Google listing/reviews when we don't have the
// exact place URL yet.
const GOOGLE_SEARCH_URL =
  "https://www.google.com/maps/search/?api=1&query=716%20Cannabis%205161%20Camp%20Rd%20Hamburg%20NY";

function Stars({ n, className = "" }: { n: number; className?: string }) {
  return (
    <span className={`inline-flex ${className}`} aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className="h-4 w-4"
          fill={i <= Math.round(n) ? "#f5b100" : "none"}
          stroke={i <= Math.round(n) ? "#f5b100" : "#d1d5db"}
        />
      ))}
    </span>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState<ReviewsData | null>(null);

  useEffect(() => {
    fetch("/api/google-reviews")
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ configured: false }));
  }, []);

  const reviews = data?.reviews || [];
  const hasLive = !!data?.configured && reviews.length > 0;
  const googleUrl = data?.googleUrl || GOOGLE_SEARCH_URL;

  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-8">
          <span className="inline-block px-4 py-1 rounded-full bg-primary_green/10 text-primary_green font-semibold text-sm mb-3">
            REVIEWS
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-secondary_green font-staatliches tracking-wide">
            LOVED IN WESTERN NEW YORK
          </h2>
          {/* Rating summary — only shows real numbers when live data is present */}
          {hasLive && data?.rating ? (
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl font-bold text-gray-900">{data.rating.toFixed(1)}</span>
              <Stars n={data.rating} />
              {data.total ? (
                <span className="text-gray-500 text-sm">({data.total} Google reviews)</span>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Stars n={5} />
              <span className="text-gray-600 text-sm">Real reviews from our community</span>
            </div>
          )}
        </div>

        {/* Live review cards */}
        {hasLive && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {reviews.slice(0, 6).map((rev, i) => (
              <div
                key={i}
                className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-3">
                  {rev.profilePhoto ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={rev.profilePhoto} alt={rev.author} className="h-10 w-10 rounded-full" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary_green/15 text-primary_green flex items-center justify-center font-bold">
                      {rev.author?.[0]?.toUpperCase() || "G"}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{rev.author}</p>
                    <div className="flex items-center gap-2">
                      <Stars n={rev.rating} />
                      <span className="text-xs text-gray-400">{rev.relativeTime}</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed line-clamp-5">{rev.text}</p>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-8">
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border-2 border-primary_green text-primary_green font-semibold hover:bg-primary_green hover:text-white transition-colors"
          >
            Read our reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
}
